import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import SidewaysLoading from 'components/chat/sidebar/SidewaysLoading';
import './initial-here-button.scss';

interface InitialHereButtonProps {
  onConfirm: () => Promise<void>;
  onComplete?: () => void;
}

// Helper to get initials from a name
const getInitials = (name?: string): string => {
  if (!name) return 'X';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Helper to detect touch device
const isTouchDevice = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

const InitialHereButton: React.FC<InitialHereButtonProps> = ({
  onConfirm,
  onComplete,
}) => {
  const { user } = useAuth0();
  const [signingState, setSigningState] = useState<
    'idle' | 'processing' | 'signed'
  >('idle');

  const userInitials = getInitials(user?.name);

  const handleClick = async () => {
    if (signingState !== 'idle') return;

    setSigningState('processing');
    try {
      await onConfirm();
      setSigningState('signed');
      // Call onComplete after showing signed state
      setTimeout(() => {
        onComplete?.();
      }, 1000);
    } catch (error) {
      console.error('Error confirming:', error);
      setSigningState('idle');
    }
  };

  return (
    <div className='initial-here-wrapper'>
      <div
        className={`initial-here-button ${signingState}`}
        onClick={handleClick}
      >
        <div className='initial-here-content'>
          <span
            className={`initials ${signingState === 'signed' ? 'signed' : 'faded'}`}
          >
            {signingState === 'processing' ? <SidewaysLoading /> : userInitials}
          </span>
          {signingState === 'idle' && (
            <span className='initial-here-text'>INITIAL HERE</span>
          )}
        </div>
      </div>
      {signingState === 'idle' && (
        <p className='initial-here-instruction'>
          {isTouchDevice() ? 'Tap' : 'Click'} to initial and approve
        </p>
      )}
      {signingState === 'signed' && (
        <p className='initial-here-date'>
          {new Date().toLocaleDateString('en-GB', {
            weekday: 'short',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </p>
      )}
    </div>
  );
};

export default InitialHereButton;
